import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Globe, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KILTRO_ASSETS, KILTRO_BRAND } from "../lib/assets";
import { storage } from "../lib/api";
import { FEATURE_FLAGS } from "../lib/featureFlags";
import EmailNicknameModal from "./EmailNicknameModal";

export default function Footer() {
  const [editUser, setEditUser] = useState(false);
  const [, setTick] = useState(0);
  const nickname = storage.getUserNickname() || storage.getHandle();
  const { t } = useTranslation();
  return (
    <footer className="border-t-2 border-[#1A1A1A] bg-[#0A0A0A] mt-16 relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-3">
            <img
              src={KILTRO_ASSETS.logo}
              alt="Kiltro"
              className="w-14 h-14 object-contain"
            />
            <div>
              <div className="font-display text-3xl leading-none">
                <span className="text-white">Kiltro</span> <span className="text-[#FEBA01]">Scout</span>
              </div>
              <div className="font-mono text-[10px] text-[#FEBA01] uppercase tracking-[0.2em] mt-1">Est. {KILTRO_BRAND.founded}</div>
            </div>
          </div>
          <p className="mt-4 text-[#A3A3A3] text-sm leading-relaxed">
            {t("footer.tagline")}
          </p>
          <div className="mt-4 flex items-center gap-3 text-[#FEBA01]">
            <a href={`mailto:${KILTRO_BRAND.contactEmail}`} className="hover:text-white transition-colors" data-testid="footer-email">
              <Mail size={18} />
            </a>
            <a href={`https://www.${KILTRO_BRAND.domain}`} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-website">
              <Globe size={18} />
            </a>
            <a href="https://instagram.com/kiltroscout" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">
              <Instagram size={18} />
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-body font-bold text-sm uppercase tracking-widest text-[#FEBA01] mb-3">{t("footer.explore")}</h4>
          <ul className="space-y-2 text-sm text-[#A3A3A3]">
            <li><Link to="/descubrir" className="hover:text-[#FEBA01]">{t("nav.discover")}</Link></li>
            <li><Link to="/rutas" className="hover:text-[#FEBA01]">{t("nav.routes")}</Link></li>
            <li><Link to="/cupones" className="hover:text-[#FEBA01]">{t("nav.coupons")}</Link></li>
            <li><Link to="/cervezas" className="hover:text-[#FEBA01]">{t("nav.beers")}</Link></li>
            <li><Link to="/nosotros" className="hover:text-[#FEBA01]" data-testid="footer-link-nosotros">{t("nav.about")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-body font-bold text-sm uppercase tracking-widest text-[#FEBA01] mb-3">{t("footer.communityTitle")}</h4>
          <ul className="space-y-2 text-sm text-[#A3A3A3]">
            <li><Link to="/comunidad" className="hover:text-[#FEBA01]">{t("footer.feed")}</Link></li>
            <li><span>{t("footer.events")}</span></li>
            <li><span>{t("footer.ambassadors")}</span></li>
          </ul>
        </div>
        <div>
          <h4 className="font-display text-base uppercase tracking-wider text-[#FEBA01] mb-3">{t("footer.forBusiness")}</h4>
          <ul className="space-y-2 text-sm text-[#A3A3A3] mb-4">
            <li><Link to="/locales" className="hover:text-[#FEBA01]" data-testid="footer-link-locales">{t("footer.barsPubs")}</Link></li>
            {FEATURE_FLAGS.brandsPublic && (
              <li><Link to="/marcas" className="hover:text-[#FEBA01]" data-testid="footer-link-marcas">{t("footer.brands")}</Link></li>
            )}
          </ul>
          <Link
            to="/locales"
            className="inline-block px-4 py-2 border-2 border-[#FEBA01] text-[#FEBA01] font-display uppercase tracking-wide text-sm hover:bg-[#FEBA01] hover:text-black transition-colors"
            data-testid="footer-contact-cta"
          >
            {t("footer.addVenue")}
          </Link>
          <div className="mt-3 font-mono text-[10px] text-[#A3A3A3] uppercase tracking-widest break-all">
            {KILTRO_BRAND.contactEmail}
          </div>
        </div>
      </div>
      <div className="border-t border-[#1A1A1A] px-4 py-4 text-[11px] text-[#666] leading-relaxed" data-testid="footer-privacy-note">
        Para dejar de compartir tus datos, escríbenos a{" "}
        <a href={`mailto:${KILTRO_BRAND.contactEmail}`} className="text-[#FEBA01] hover:underline">
          {KILTRO_BRAND.contactEmail}
        </a>.
      </div>
      <div className="border-t border-[#1A1A1A] py-4 px-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#A3A3A3] font-mono uppercase tracking-widest">
        <span>© {KILTRO_BRAND.founded} Kiltro Scout · {KILTRO_BRAND.domain} · {t("footer.madeWith")}</span>
        <button
          type="button"
          onClick={() => setEditUser(true)}
          className="inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-[#FEBA01]"
          data-testid="footer-edit-handle"
        >
          <User size={12} /> {nickname ? `${t("footer.signsAs")} ${nickname}` : t("footer.pickAlias")}
        </button>
      </div>
      <EmailNicknameModal
        open={editUser}
        onClose={() => setEditUser(false)}
        onDone={() => setTick((t) => t + 1)}
        title={nickname ? "Actualizar mis datos" : "Únete a la camada"}
      />
    </footer>
  );
}
